import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import { useAuth } from '../lib/auth';
import { UserPlus, Trash2, Key, ShieldCheck, User, Lock, Check, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter 86 — Access tab.
 *
 * Admin-only surface for provisioning site operators. Only users with
 * role='admin' can reach this page; the sidebar hides it for everyone
 * else, and the backend enforces the same on every endpoint.
 *
 * Flow:
 *   1. List existing users (GET /access/users)
 *   2. Add new user via inline form (POST /access/users)
 *   3. Reset password / toggle role / disable via row actions (PATCH)
 *   4. Delete via trash button (DELETE — seed admin protected)
 */
export default function Access() {
  const { user: me, role } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [last, setLast] = useState(null);

  // Add-user form
  const [form, setForm] = useState({ username: '', password: '', role: 'operator' });
  const [submitting, setSubmitting] = useState(false);

  // Password-reset inline state — { [username]: newPasswordString } while
  // the operator is typing a replacement password.
  const [resetting, setResetting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/access/users');
      setUsers(data.users || []);
      setLast(new Date().toISOString());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (role === 'admin') load(); }, [role]);

  if (role && role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <ShieldCheck className="mx-auto mb-4 text-sell" size={32} />
        <div className="text-sell text-lg tracking-widest uppercase font-bold">Access denied</div>
        <div className="text-[var(--txt-secondary)] text-sm mt-2">This tab is restricted to admin operators.</div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const u = form.username.trim();
    const p = form.password;
    if (!u || u.length < 3) { toast.error('Username must be at least 3 characters'); return; }
    if (!p || p.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    try {
      await api.post('/access/users', { username: u, password: p, role: form.role });
      toast.success(`User "${u}" created`);
      setForm({ username: '', password: '', role: 'operator' });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetPassword = async (username) => {
    const pw = (resetting?.pw || '').trim();
    if (!pw || pw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await api.patch(`/access/users/${username}`, { password: pw });
      toast.success(`Password reset for "${username}"`);
      setResetting(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    }
  };

  const toggleRole = async (u) => {
    const nextRole = u.role === 'admin' ? 'operator' : 'admin';
    try {
      await api.patch(`/access/users/${u.username}`, { role: nextRole });
      toast.success(`Role → ${nextRole}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Role change failed');
    }
  };

  const toggleDisabled = async (u) => {
    try {
      await api.patch(`/access/users/${u.username}`, { disabled: !u.disabled });
      toast.success(u.disabled ? `Enabled "${u.username}"` : `Disabled "${u.username}"`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toggle failed');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/access/users/${u.username}`);
      toast.success(`Deleted "${u.username}"`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="access-page">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] text-[var(--txt-muted)] uppercase tracking-widest">// ATDE.access.v1</div>
          <div className="text-cyan-sys text-lg font-bold tracking-widest uppercase mt-0.5">Site Access Control</div>
        </div>
        <RefreshButton loading={loading} last={last} onClick={load} data-testid="access-refresh" />
      </div>

      {/* Add-user form */}
      <CornerPanel title="Provision New Operator">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="access-add-form">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">Username</label>
            <div className="relative">
              <User size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--txt-muted)]" />
              <input
                data-testid="access-add-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="new.operator"
                autoComplete="off"
                maxLength={32}
                className="w-full bg-black border border-[var(--border-default)] focus:border-cyan-sys pl-7 pr-2 py-1.5 font-mono text-[12px] text-cyan-sys outline-none"
              />
            </div>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">Password</label>
            <div className="relative">
              <Lock size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--txt-muted)]" />
              <input
                data-testid="access-add-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="min 6 chars"
                autoComplete="new-password"
                maxLength={128}
                className="w-full bg-black border border-[var(--border-default)] focus:border-cyan-sys pl-7 pr-2 py-1.5 font-mono text-[12px] text-cyan-sys outline-none"
              />
            </div>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">Role</label>
            <select
              data-testid="access-add-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full bg-black border border-[var(--border-default)] focus:border-cyan-sys px-2 py-1.5 font-mono text-[12px] text-cyan-sys outline-none"
            >
              <option value="operator">operator</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={submitting}
              data-testid="access-add-submit"
              className="w-full bg-cyan-sys/10 border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <UserPlus size={12} /> {submitting ? 'creating…' : 'create user'}
            </button>
          </div>
        </form>
        <div className="mt-2 text-[10px] text-[var(--txt-muted)]">
          Give each teammate their own credentials. They log in from the same login screen using the username + password you set here.
        </div>
      </CornerPanel>

      {/* User list */}
      <CornerPanel title={`Authorised Operators // ${users.length}`}>
        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[820px]" data-testid="access-users-table">
            <thead>
              <tr className="border-b border-[var(--border-default)] text-[10px] uppercase tracking-widest text-[var(--txt-muted)]">
                <th className="text-left py-2 px-2">Username</th>
                <th className="text-left py-2 px-2">Role</th>
                <th className="text-left py-2 px-2">Status</th>
                <th className="text-left py-2 px-2">Created</th>
                <th className="text-left py-2 px-2">Created By</th>
                <th className="text-right py-2 px-2 w-[280px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-[var(--txt-muted)]">
                  {loading ? 'loading…' : 'no users yet'}
                </td></tr>
              )}
              {users.map((u) => {
                const isResetting = resetting?.username === u.username;
                const isMe = u.username === me;
                return (
                  <tr key={u.username} className="border-b border-[var(--border-default)]" data-testid={`access-row-${u.username}`}>
                    <td className="py-2 px-2 font-bold text-cyan-sys">
                      {u.username}
                      {u.is_seed && <span className="ml-2 text-[9px] uppercase tracking-widest text-hold px-1 border border-hold/40" title="seed admin — cannot be deleted or demoted">seed</span>}
                      {isMe && <span className="ml-2 text-[9px] uppercase tracking-widest text-buy px-1 border border-buy/40">you</span>}
                    </td>
                    <td className="py-2 px-2">
                      <span className={`uppercase tracking-widest text-[10px] px-1.5 py-0.5 border ${u.role === 'admin' ? 'text-hold border-hold/60' : 'text-[var(--txt-secondary)] border-[var(--border-default)]'}`}
                            data-testid={`access-role-${u.username}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <span className={`uppercase tracking-widest text-[10px] ${u.disabled ? 'text-sell' : 'text-buy'}`}
                            data-testid={`access-status-${u.username}`}>
                        {u.disabled ? '● disabled' : '● active'}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-[var(--txt-secondary)] text-[10px]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2 px-2 text-[var(--txt-secondary)] text-[10px]">
                      {u.created_by || '—'}
                    </td>
                    <td className="py-2 px-2">
                      {isResetting ? (
                        <div className="flex items-center gap-1 justify-end">
                          <input
                            data-testid={`access-reset-input-${u.username}`}
                            type="password"
                            placeholder="new password"
                            autoFocus
                            value={resetting.pw}
                            onChange={(e) => setResetting({ ...resetting, pw: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter') resetPassword(u.username); if (e.key === 'Escape') setResetting(null); }}
                            className="w-36 bg-black border border-cyan-sys text-cyan-sys outline-none px-2 py-1 font-mono text-[11px]"
                          />
                          <button
                            onClick={() => resetPassword(u.username)}
                            data-testid={`access-reset-save-${u.username}`}
                            className="text-buy hover:underline text-[11px] inline-flex items-center gap-0.5"
                          >
                            <Check size={12} /> save
                          </button>
                          <button
                            onClick={() => setResetting(null)}
                            data-testid={`access-reset-cancel-${u.username}`}
                            className="text-[var(--txt-muted)] hover:text-sell text-[11px]"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 justify-end">
                          <button
                            onClick={() => setResetting({ username: u.username, pw: '' })}
                            data-testid={`access-reset-${u.username}`}
                            className="text-cyan-sys hover:underline text-[11px] inline-flex items-center gap-0.5"
                            title="reset password"
                          >
                            <Key size={11} /> reset
                          </button>
                          {!u.is_seed && !isMe && (
                            <>
                              <button
                                onClick={() => toggleRole(u)}
                                data-testid={`access-toggle-role-${u.username}`}
                                className="text-hold hover:underline text-[11px]"
                                title={`Make ${u.role === 'admin' ? 'operator' : 'admin'}`}
                              >
                                → {u.role === 'admin' ? 'operator' : 'admin'}
                              </button>
                              <button
                                onClick={() => toggleDisabled(u)}
                                data-testid={`access-toggle-disabled-${u.username}`}
                                className={`hover:underline text-[11px] ${u.disabled ? 'text-buy' : 'text-hold'}`}
                              >
                                {u.disabled ? 'enable' : 'disable'}
                              </button>
                              <button
                                onClick={() => remove(u)}
                                data-testid={`access-delete-${u.username}`}
                                className="text-sell hover:underline text-[11px] inline-flex items-center gap-0.5"
                                title="delete user"
                              >
                                <Trash2 size={11} /> delete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CornerPanel>
    </div>
  );
}
